/**
 * HALO Badge Component
 *
 * A versatile badge component following Constellation v10 patterns.
 * Supports both static display badges and interactive button-like badges.
 *
 * Features:
 * - Three tone variants (primary, alert, inactive)
 * - Two sizes (sm, md) with 25px standard height
 * - Optional icon support with proper spacing
 * - Interactive mode with full accessibility support
 * - Proper keyboard navigation and focus management
 * - Dual-mode rendering (span vs button) based on onClick presence
 */

import React from 'react';
import { badgeBase, badgeVariants, iconWrapper } from './badge.styles';
import type { BadgeProps, isInteractiveBadge } from './types';

/**
 * Type guard to determine if badge should be interactive
 * Used internally to switch between span and button rendering
 */
function getIsInteractive(props: BadgeProps): boolean {
  return typeof props.onClick === 'function';
}

export const Badge = React.forwardRef<HTMLSpanElement | HTMLButtonElement, BadgeProps>(
  ({ tone = 'primary', size = 'md', icon, disabled = false, onClick, children, className, ...restProps }, ref) => {
    const isInteractive = getIsInteractive({ tone, size, icon, disabled, onClick, children, className, ...restProps });

    // Combine base styles with variants using cva
    const badgeClasses = [
      badgeBase,
      badgeVariants({
        tone,
        size,
        interactive: isInteractive,
      }),
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Handle click events for interactive badges
    const handleClick = () => {
      if (!disabled && onClick) {
        onClick();
      }
    };

    // Handle keyboard events for accessibility
    // Enter and Space keys should trigger onClick like native buttons
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (isInteractive && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        handleClick();
      }
    };

    // Render icon with proper wrapper and screen reader hiding
    const renderIcon = (iconElement: React.ReactNode) => (
      <span className={iconWrapper} aria-hidden="true">
        {iconElement}
      </span>
    );

    // Render badge content with proper icon placement
    const renderContent = () => (
      <>
        {icon && renderIcon(icon)}
        <span>{children}</span>
      </>
    );

    // Render interactive badge as button element
    if (isInteractive) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          role="button"
          className={badgeClasses}
          disabled={disabled}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          aria-disabled={disabled}
          {...(restProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        >
          {renderContent()}
        </button>
      );
    }

    // Render static badge as span element
    return (
      <span
        ref={ref as React.Ref<HTMLSpanElement>}
        className={badgeClasses}
        {...(restProps as React.HTMLAttributes<HTMLSpanElement>)}
      >
        {renderContent()}
      </span>
    );
  },
);

Badge.displayName = 'Badge';

// Export types for consumers
export type { BadgeProps, BadgeTone, BadgeSize } from './types';
